import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { getLoungeProfile, saveLoungeProfile, trackLoungeEvent } from "@/lib/loungeTrack";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { loungeCopy } from "@/lib/loungeCopy";
import { LoungeSectionTitle } from "@/components/lounge/LoungeShell";
import { resolveLoungeUrl } from "@/lib/loungeUrl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Plus, Minus, Trash2, Send, Info, PackageCheck, Image as ImageIcon, Eye, ZoomIn, X, ChevronLeft, ChevronRight, Languages } from "lucide-react";

const sizesOf = (a) => (Array.isArray(a?.sizes) ? a.sizes.filter(Boolean) : []);

function Meta({ label, value }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="font-medium text-foreground">{value}</p>
    </div>
  );
}

// Scheda dettaglio prodotto (sola consultazione) con gallery multi-foto, replicando
// il dialog del Portale B2B MA senza prezzi né giacenze.
function LoungeDetailDialog({ article, open, onOpenChange, onAdd, c }) {
  const { lang } = useI18n();
  const [active, setActive] = useState(0);
  const [lbIndex, setLbIndex] = useState(null);
  const [size, setSize] = useState("");
  const [trDesc, setTrDesc] = useState(null);
  const [trSpecs, setTrSpecs] = useState(null);
  const [translating, setTranslating] = useState(false);
  const images = Array.isArray(article?.images) ? article.images : [];
  const sizes = sizesOf(article);
  const hasSizes = sizes.length > 0;
  const colors = Array.isArray(article?.colors) ? article.colors : [];
  const desc = (article?.description || "").trim();
  const specs = (article?.tech_specs || "").trim();

  useEffect(() => {
    if (open) { setActive(0); setSize(""); setLbIndex(null); }
  }, [open, article?.name]);

  useEffect(() => {
    if (!open || lang === "it" || (!desc && !specs)) { setTrDesc(null); setTrSpecs(null); setTranslating(false); return; }
    let cancelled = false;
    const payload = [];
    if (desc) payload.push(desc);
    if (specs && specs !== desc) payload.push(specs);
    setTranslating(true);
    api.post("/public/translate", { texts: payload, target_lang: lang })
      .then(({ data }) => {
        if (cancelled) return;
        const tr = data.translations || [];
        let idx = 0;
        if (desc) { setTrDesc(tr[idx] ?? desc); idx += 1; }
        if (specs && specs !== desc) { setTrSpecs(tr[idx] ?? specs); }
      })
      .catch(() => { if (!cancelled) { setTrDesc(null); setTrSpecs(null); } })
      .finally(() => { if (!cancelled) setTranslating(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lang, article?.name]);

  if (!article) return null;
  const mainImg = resolveLoungeUrl(images[active] || images[0]);
  const showTr = lang !== "it";
  const descShown = showTr && !translating && trDesc ? trDesc : desc;
  const specsShown = showTr && !translating && trSpecs ? trSpecs : specs;

  const add = () => {
    if (hasSizes && !size) return toast.error(c.select_size_toast);
    onAdd({ name: article.name, line: article.line || "", size: hasSizes ? size : "" });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto border-border bg-card" data-testid="sample-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-foreground">{c.detail_title}</DialogTitle>
          <DialogDescription className="sr-only">{article.name}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Gallery */}
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => mainImg && setLbIndex(active)}
              data-testid="sample-detail-zoom"
              className="group relative block aspect-square w-full cursor-zoom-in overflow-hidden rounded-xl border border-border bg-muted"
            >
              {mainImg ? (
                <img src={mainImg} alt={article.name} className="h-full w-full object-cover" data-testid="sample-detail-main-image" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-muted-foreground"><ImageIcon className="h-12 w-12" /></div>
              )}
              {article.category && (
                <span className="absolute left-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur">{article.category}</span>
              )}
              {mainImg && (
                <span className="pointer-events-none absolute bottom-2 right-2 flex items-center gap-1 rounded-full bg-black/55 px-2 py-1 text-[11px] text-white opacity-80 backdrop-blur transition-opacity group-hover:opacity-100">
                  <ZoomIn className="h-3.5 w-3.5" /> {c.zoom_hint}
                </span>
              )}
            </button>
            {images.length > 1 && (
              <div className="grid grid-cols-4 gap-2" data-testid="sample-detail-thumbs">
                {images.map((im, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActive(i)}
                    data-testid={`sample-detail-thumb-${i}`}
                    className={`aspect-square overflow-hidden rounded-lg border bg-muted transition ${i === active ? "border-primary ring-1 ring-primary" : "border-border hover:border-primary/50"}`}
                  >
                    <img src={resolveLoungeUrl(im)} alt={`${article.name} ${i + 1}`} className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info (read-only, senza prezzi) */}
          <div className="space-y-5">
            <div>
              <h3 className="text-xl font-bold text-foreground" data-testid="sample-detail-name">{article.name}</h3>
              {(article.line || article.model_code) && (
                <p className="text-sm text-muted-foreground">
                  {article.line && <span className="font-bold uppercase tracking-wide text-foreground">{article.line}</span>}
                  {article.line && article.model_code ? " · " : ""}{article.model_code}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              {article.category && <Meta label={c.category_label} value={article.category} />}
              {article.line && <Meta label={c.line_label} value={article.line} />}
              {article.model_code && <Meta label={c.code_label} value={article.model_code} />}
              {article.gender && <Meta label={c.gender_label} value={article.gender} />}
              {colors.length > 0 && <Meta label={c.colors} value={colors.join(", ")} />}
              {sizes.length > 0 && <Meta label={c.sizes} value={sizes.join(", ")} />}
            </div>

            <div className="space-y-2 rounded-lg border border-border bg-muted/50 p-3" data-testid="sample-detail-order-box">
              {hasSizes && (
                <Select value={size} onValueChange={setSize}>
                  <SelectTrigger className="h-9" data-testid="sample-detail-size">
                    <SelectValue placeholder={c.select_size_ph} />
                  </SelectTrigger>
                  <SelectContent>
                    {sizes.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`sample-detail-size-opt-${s}`}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button
                data-testid="sample-detail-add"
                onClick={add}
                className="w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90"
              >
                <Plus className="mr-1 h-4 w-4" /> {c.add_to_selection}
              </Button>
            </div>

            {showTr && (desc || specs) && (
              <div className="flex items-center gap-1.5 text-[11px] font-medium text-brand" data-testid="sample-detail-translated-badge">
                {translating ? <><Loader2 className="h-3 w-3 animate-spin" /> {c.translating}</> : <><Languages className="h-3 w-3" /> {c.auto_translated}</>}
              </div>
            )}
            {desc && (
              <div>
                <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-brand">{c.description_label}</h4>
                <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground" data-testid="sample-detail-desc">{descShown}</p>
              </div>
            )}
            {specs && specs !== desc && (
              <div>
                <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-brand">{c.tech_label}</h4>
                <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground" data-testid="sample-detail-specs">{specsShown}</p>
              </div>
            )}
            {!desc && !specs && <p className="text-sm text-muted-foreground">{c.no_description}</p>}
          </div>
        </div>

        {/* Lightbox */}
        {lbIndex !== null && images[lbIndex] && (
          <div data-testid="sample-detail-lightbox" onClick={() => setLbIndex(null)} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4">
            <img src={resolveLoungeUrl(images[lbIndex])} alt={article.name} className="max-h-[92vh] max-w-[92vw] rounded-lg object-contain shadow-2xl" />
            <button type="button" onClick={(e) => { e.stopPropagation(); setLbIndex(null); }} data-testid="sample-detail-lightbox-close" className="absolute right-4 top-4 rounded-full bg-white/15 p-2 text-white transition hover:bg-white/30">
              <X className="h-5 w-5" />
            </button>
            {images.length > 1 && (
              <>
                <button type="button" onClick={(e) => { e.stopPropagation(); setLbIndex((lbIndex - 1 + images.length) % images.length); }} data-testid="sample-detail-lightbox-prev" className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/15 p-2.5 text-white transition hover:bg-white/30">
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button type="button" onClick={(e) => { e.stopPropagation(); setLbIndex((lbIndex + 1) % images.length); }} data-testid="sample-detail-lightbox-next" className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/15 p-2.5 text-white transition hover:bg-white/30">
                  <ChevronRight className="h-6 w-6" />
                </button>
                <span className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs text-white" data-testid="sample-detail-lightbox-counter">{lbIndex + 1} / {images.length}</span>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Card prodotto: replica visiva della ProductCard del Portale B2B (badge, codice,
// descrizione, gallery nel dialog, gestione taglie) SENZA prezzi né giacenze.
function SampleCard({ article, index, onAdd, delay, c }) {
  const [size, setSize] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const sizes = sizesOf(article);
  const hasSizes = sizes.length > 0;
  const img = resolveLoungeUrl((article.images || [])[0]);

  const add = () => {
    if (hasSizes && !size) return toast.error(c.select_size_toast);
    onAdd({ name: article.name, line: article.line || "", size: hasSizes ? size : "" });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: Math.min(delay, 0.3) }}
      data-testid={`sample-card-${index}`}
      className="flex flex-col overflow-hidden rounded-xl border border-border bg-card"
    >
      <button type="button" onClick={() => setDetailOpen(true)} data-testid={`sample-detail-trigger-${index}`} className="group relative aspect-square overflow-hidden bg-muted text-left">
        {img ? (
          <img src={img} alt={article.name} className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground"><ImageIcon className="h-10 w-10" /></div>
        )}
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 text-transparent transition-colors group-hover:bg-black/25 group-hover:text-white">
          <Eye className="h-6 w-6" />
        </span>
      </button>
      <div className="flex flex-1 flex-col p-4">
        <button type="button" onClick={() => setDetailOpen(true)} data-testid={`sample-name-${index}`} className="text-left font-semibold text-foreground transition-colors hover:text-brand">
          {article.name}
        </button>
        {(article.line || article.model_code) && (
          <p className="text-xs text-muted-foreground" data-testid={`sample-line-${index}`}>
            {article.line && <span className="font-bold uppercase tracking-wide text-foreground">{article.line}</span>}
            {article.model_code ? `${article.line ? " · " : ""}${article.model_code}` : ""}
          </p>
        )}
        {article.tech_specs && <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{article.tech_specs}</p>}

        <div className="mt-auto space-y-2 pt-3">
          {hasSizes && (
            <Select value={size} onValueChange={setSize}>
              <SelectTrigger className="h-9" data-testid={`sample-size-${index}`}><SelectValue placeholder={c.select_size_ph} /></SelectTrigger>
              <SelectContent>
                {sizes.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`sample-size-${index}-opt-${s}`}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button size="sm" onClick={add} data-testid={`sample-add-${index}`} className="w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
            <Plus className="mr-1 h-4 w-4" /> {c.add_to_selection}
          </Button>
        </div>
      </div>
      <LoungeDetailDialog article={article} open={detailOpen} onOpenChange={setDetailOpen} onAdd={(it) => { onAdd(it); setDetailOpen(false); }} c={c} />
    </motion.div>
  );
}

// Sezione pubblica "Selezione Campioni": griglia prodotti (stile Portale, 4 colonne,
// senza dati economici) + pannello selezione. All'invio salva richiesta "Campionatura".
export function SampleSelection({ sourcePage, refValue }) {
  const { lang } = useI18n();
  const c = loungeCopy(lang);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picks, setPicks] = useState([]);
  const [contact, setContact] = useState({ name: "", email: "", address: "", phone: "", message: "", reference: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/public/catalog")
      .then(({ data }) => setArticles(data.articles || []))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  const setContactField = (k) => (e) => setContact((v) => ({ ...v, [k]: e.target.value }));

  const addPick = ({ name, line, size }) => {
    setPicks((prev) => {
      const i = prev.findIndex((p) => p.name === name && p.size === size);
      if (i >= 0) {
        const next = [...prev];
        next[i] = { ...next[i], qty: next[i].qty + 1 };
        return next;
      }
      return [...prev, { name, line: line || "", size: size || "", qty: 1 }];
    });
    toast.success(c.sample_added);
  };
  const inc = (i) => setPicks((p) => p.map((it, j) => (j === i ? { ...it, qty: it.qty + 1 } : it)));
  const dec = (i) => setPicks((p) => p.flatMap((it, j) => (j === i ? (it.qty > 1 ? [{ ...it, qty: it.qty - 1 }] : []) : [it])));
  const removeAt = (i) => setPicks((p) => p.filter((_, j) => j !== i));
  const count = picks.reduce((s, it) => s + it.qty, 0);

  const submit = async (e) => {
    e.preventDefault();
    if (picks.length === 0) return toast.error(c.sample_need_items);
    if (!contact.name.trim() || !contact.email.includes("@") || !contact.address.trim() || !contact.phone.trim() || !contact.message.trim())
      return toast.error(c.fill_required);
    setSending(true);
    try {
      await api.post("/public/lounge/sample-request", {
        name: contact.name.trim(),
        email: contact.email.trim(),
        address: contact.address.trim(),
        phone: contact.phone.trim(),
        message: contact.message.trim(),
        social: sourcePage === "ambassador" ? contact.reference.trim() : "",
        organization: sourcePage === "ambassador" ? "" : contact.reference.trim(),
        samples: picks.map((it) => ({ name: it.name, line: it.line, size: it.size, qty: it.qty })),
        ref: refValue || "",
        source_page: sourcePage,
        lang,
      });
      if (sourcePage === "ambassador") {
        if (!getLoungeProfile("ambassador")?.email) saveLoungeProfile("ambassador", { name: contact.name.trim(), email: contact.email.trim(), social: contact.reference.trim() });
        trackLoungeEvent({ area: "ambassador", type: "access", item: { kind: "form", title: "Richiesta campioni" }, lang });
      }
      toast.success(c.success);
      setPicks([]);
      setContact({ name: "", email: "", address: "", phone: "", message: "", reference: "" });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || c.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="campioni" className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24" data-testid="lounge-samples">
      <LoungeSectionTitle eyebrow={c.samples_eyebrow} title={c.samples_title} subtitle={c.samples_sub} testId="lounge-samples-title" />

      {loading ? (
        <div className="flex items-center gap-2 py-10 text-muted-foreground" data-testid="lounge-samples-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> …
        </div>
      ) : articles.length === 0 ? (
        <p className="py-10 text-muted-foreground" data-testid="lounge-samples-empty">{c.empty_catalog}</p>
      ) : (
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Griglia prodotti (stile Portale B2B) */}
          <div className="lg:col-span-2">
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {articles.map((a, i) => (
                <SampleCard key={i} article={a} index={i} delay={i * 0.03} onAdd={addPick} c={c} />
              ))}
            </div>
          </div>

          {/* Pannello selezione */}
          <aside className="lg:sticky lg:top-24 lg:self-start" data-testid="sample-panel">
            <div className="rounded-2xl border border-border bg-card p-5 sm:p-6">
              <div className="flex items-center gap-2.5 border-b border-border pb-4">
                <PackageCheck className="h-5 w-5 text-brand" />
                <h3 className="text-lg font-bold text-foreground">{c.panel_title}</h3>
                {count > 0 && (
                  <span className="ml-auto rounded-full bg-brand/15 px-2.5 py-0.5 text-xs font-bold text-brand" data-testid="sample-panel-count">{count}</span>
                )}
              </div>

              {picks.length === 0 ? (
                <p className="py-6 text-sm text-muted-foreground" data-testid="sample-panel-empty">{c.panel_empty}</p>
              ) : (
                <ul className="divide-y divide-border" data-testid="sample-panel-list">
                  {picks.map((it, i) => (
                    <li key={`${it.name}-${it.size}`} className="flex items-center gap-3 py-3" data-testid={`sample-row-${i}`}>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-foreground">{it.name}</div>
                        <div className="text-xs text-muted-foreground">{it.size ? `${c.select_size}: ${it.size}` : c.sizes_none}</div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button type="button" onClick={() => dec(i)} className="flex h-7 w-7 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:bg-brema-hover" data-testid={`sample-dec-${i}`} aria-label="-">
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-5 text-center text-sm font-semibold text-foreground" data-testid={`sample-qty-${i}`}>{it.qty}</span>
                        <button type="button" onClick={() => inc(i)} className="flex h-7 w-7 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:bg-brema-hover" data-testid={`sample-inc-${i}`} aria-label="+">
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <button type="button" onClick={() => removeAt(i)} className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-brema-red" data-testid={`sample-remove-${i}`} aria-label="remove">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <form onSubmit={submit} className="mt-5 space-y-3 border-t border-border pt-5" data-testid="sample-form">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{c.contact_heading}</div>
                <div>
                  <Label className="mb-1 block text-xs text-muted-foreground">{c.f_company} <span className="text-brand">*</span></Label>
                  <Input value={contact.name} onChange={setContactField("name")} placeholder={c.f_company} data-testid="sample-field-name" />
                </div>
                <div>
                  <Label className="mb-1 block text-xs text-muted-foreground">{sourcePage === "ambassador" ? c.f_social : c.f_outlet}</Label>
                  <Input value={contact.reference} onChange={setContactField("reference")} placeholder={sourcePage === "ambassador" ? c.f_social : c.f_outlet} data-testid="sample-field-reference" />
                </div>
                <div>
                  <Label className="mb-1 block text-xs text-muted-foreground">{c.f_address} <span className="text-brand">*</span></Label>
                  <Input value={contact.address} onChange={setContactField("address")} placeholder={c.f_address} data-testid="sample-field-address" />
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <Label className="mb-1 block text-xs text-muted-foreground">{c.f_email} <span className="text-brand">*</span></Label>
                    <Input type="email" value={contact.email} onChange={setContactField("email")} placeholder={c.f_email} data-testid="sample-field-email" />
                  </div>
                  <div>
                    <Label className="mb-1 block text-xs text-muted-foreground">{c.f_phone} <span className="text-brand">*</span></Label>
                    <Input value={contact.phone} onChange={setContactField("phone")} placeholder={c.f_phone} data-testid="sample-field-phone" />
                  </div>
                </div>
                <div>
                  <Label className="mb-1 block text-xs text-muted-foreground">{c.f_message} <span className="text-brand">*</span></Label>
                  <Textarea rows={3} value={contact.message} onChange={setContactField("message")} placeholder={c.f_message} data-testid="sample-field-message" />
                </div>

                <div className="flex gap-2.5 rounded-xl border border-brand/30 bg-brand/10 p-3 text-xs leading-relaxed text-muted-foreground" data-testid="sample-disclaimer">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                  <span>{c.disclaimer}</span>
                </div>

                {refValue && (
                  <p className="text-xs text-muted-foreground" data-testid="sample-ref">
                    {c.ref_note} <span className="font-semibold text-foreground">{refValue}</span>
                  </p>
                )}

                <Button type="submit" disabled={sending} className="w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90" data-testid="sample-submit">
                  {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                  {sending ? c.sending : c.sample_submit}
                </Button>
              </form>
            </div>
          </aside>
        </div>
      )}
    </section>
  );
}
